import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { RequireAuth, RequirePermission, PERM } from "@/features/auth";
import { AppLayout } from "@/layouts/AppLayout";
import { APP_PATH } from "@/lib/basePath";
import { FEATURES } from "@/config/featureFlags";

import LoginPage from "@/features/auth/pages/LoginPage";
import ResetPasswordPage from "@/features/auth/pages/ResetPasswordPage";
import MyAccountPage from "@/features/auth/pages/MyAccountPage";
import DashboardPage from "@/features/dashboard/pages/DashboardPage";
import MerchantsPage from "@/features/merchants/pages/MerchantsPage";
import MerchantDetailPage from "@/features/merchants/pages/MerchantDetailPage";
import UsersPage from "@/features/users/pages/UsersPage";
import UserEditPage from "@/features/users/pages/UserEditPage";
import RolesPage from "@/features/users/pages/RolesPage";
import RoleEditPage from "@/features/users/pages/RoleEditPage";
import ApplicationsPage from "@/features/applications/pages/deprecated/ApplicationsPage";
import ApplicationDetailPage from "@/features/applications/pages/deprecated/ApplicationDetailPage";
import ApplicationsV2Page from "@/features/applications/pages/ApplicationsV2Page";
import ApplicationV2WizardPage from "@/features/applications/pages/ApplicationV2WizardPage";
import BankCheckAuditPage from "@/features/applications/pages/BankCheckAuditPage";
import CampaignsPage from "@/features/applications/pages/CampaignsPage";
import CampaignEditPage from "@/features/applications/pages/CampaignEditPage";
import PublicApplyPage from "@/features/onboarding/pages/PublicApplyPage";
import BusinessVerificationPage from "@/features/onboarding/pages/BusinessVerificationPage";
import OnboardingFormPage from "@/features/onboarding/pages/OnboardingFormPage";
import VerifyFormPage from "@/features/onboarding/pages/VerifyFormPage";
import { EquipmentPage } from "@/features/equipment/pages/EquipmentPage";
import { EquipmentOrderPage, EquipmentStartPage } from "@/features/equipment/pages/EquipmentOrderPage";
import { EquipmentGate } from "@/features/equipment/components/EquipmentGate";
import PartnerFormPage from "@/features/onboarding/pages/PartnerFormPage";
import PartnerPublicPage from "@/features/onboarding/pages/PartnerPublicPage";
import SettingsPage from "@/features/settings/pages/SettingsPage";
import ImportPage from "@/features/imports/pages/ImportPage";
import ImportMerchantsPage from "@/features/imports/pages/ImportMerchantsPage";
import ImportUsersPage from "@/features/imports/pages/ImportUsersPage";
import ImportMerchantPdfPage from "@/features/imports/pages/ImportMerchantPdfPage";
import QuotesPage from "@/features/quotes/pages/QuotesPage";
import QuoteEditPage from "@/features/quotes/pages/QuoteEditPage";
import QuoteDetailPage from "@/features/quotes/pages/QuoteDetailPage";
import InvoiceViewPage from "@/features/quotes/pages/InvoiceViewPage";
import RecurringInvoicesPage from "@/features/quotes/pages/RecurringInvoicesPage";
import RecurringInvoiceEditPage from "@/features/quotes/pages/RecurringInvoiceEditPage";
import TicketsPage from "@/features/tickets/pages/TicketsPage";
import NewTicketPage from "@/features/tickets/pages/NewTicketPage";
import TicketDetailPage from "@/features/tickets/pages/TicketDetailPage";
import TicketTemplatesPage from "@/features/tickets/pages/TicketTemplatesPage";
import TicketTemplateEditPage from "@/features/tickets/pages/TicketTemplateEditPage";
import ProductsPage from "@/features/catalog/pages/ProductsPage";
import ProductEditPage from "@/features/catalog/pages/ProductEditPage";
import SalesCampaignsPage from "@/features/catalog/pages/SalesCampaignsPage";
import SalesCampaignEditPage from "@/features/catalog/pages/SalesCampaignEditPage";
import TaxRatesPage from "@/features/catalog/pages/TaxRatesPage";
import AuditLogPage from "@/features/reports/pages/AuditLogPage";
import ResidualsReportPage from "@/features/reports/pages/ResidualsReportPage";
import MaintenancePage from "@/features/system/pages/MaintenancePage";
import BranchesPage from "@/features/org/pages/BranchesPage";
import GroupsPage from "@/features/org/pages/GroupsPage";

const gate = (perm: string | string[], element: ReactNode): ReactNode => (
  <RequirePermission perm={perm}>{element}</RequirePermission>
);

const SALES_VIEW = [PERM.salesOrders, PERM.invoices];

export function AppRouter() {
  return (
    <BrowserRouter basename={APP_PATH}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/apply" element={<PublicApplyPage />} />
        <Route path="/apply/:ref" element={<PublicApplyPage />} />
        <Route path="/apply/p/:pricedRef" element={<PublicApplyPage />} />
        <Route path="/verify" element={<BusinessVerificationPage />} />
        <Route path="/verify/:ref" element={<BusinessVerificationPage />} />
        <Route path="/invoice-view/:token" element={<InvoiceViewPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/partner/:token" element={<PartnerPublicPage />} />
        <Route path="/maintenance" element={<MaintenancePage />} />

        <Route
          element={
            <RequireAuth>
              <AppLayout>
                <Outlet />
              </AppLayout>
            </RequireAuth>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/merchants" element={<MerchantsPage />} />
          <Route path="/merchants/:id" element={<MerchantDetailPage />} />
          <Route path="/manage/users" element={gate(PERM.users, <UsersPage />)} />
          <Route path="/manage/users/new" element={gate(PERM.users, <UserEditPage />)} />
          <Route path="/manage/users/:id" element={gate(PERM.users, <UserEditPage />)} />
          <Route path="/manage/roles" element={gate(PERM.roles, <RolesPage />)} />
          <Route path="/manage/roles/new" element={gate(PERM.roles, <RoleEditPage />)} />
          <Route path="/manage/roles/:id" element={gate(PERM.roles, <RoleEditPage />)} />
          <Route path="/manage/settings" element={gate(PERM.settingsSecrets, <SettingsPage />)} />
          {FEATURES.manageModule && (
            <>
              <Route path="/imports/residuals" element={gate(PERM.importsModule, <ImportPage />)} />
              <Route path="/imports/users" element={gate(PERM.importsModule, <ImportUsersPage />)} />
              <Route path="/imports/merchant-pdf" element={gate(PERM.importsModule, <ImportMerchantPdfPage />)} />
              <Route path="/imports/merchants" element={gate(PERM.importsModule, <ImportMerchantsPage />)} />
              <Route path="/manage/import" element={<Navigate to="/imports/residuals" replace />} />
            </>
          )}
          {FEATURES.salesModule && (
            <>
              <Route path="/quotes" element={gate(SALES_VIEW, <QuotesPage />)} />
              <Route path="/quotes/new" element={gate(SALES_VIEW, <QuoteEditPage />)} />
              <Route path="/quotes/:id" element={gate(SALES_VIEW, <QuoteDetailPage />)} />
              <Route path="/quotes/:id/edit" element={gate(SALES_VIEW, <QuoteEditPage />)} />
            </>
          )}
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/new" element={<NewTicketPage />} />
          <Route path="/tickets/templates" element={<TicketTemplatesPage />} />
          <Route path="/tickets/templates/new" element={<TicketTemplateEditPage />} />
          <Route path="/tickets/templates/:id/edit" element={<TicketTemplateEditPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/onboarding-form" element={<OnboardingFormPage />} />
          <Route path="/manage/partner-form" element={gate(PERM.partnerForm, <PartnerFormPage />)} />
          <Route path="/manage/branches" element={gate(PERM.branches, <BranchesPage />)} />
          <Route path="/manage/groups" element={gate(PERM.groups, <GroupsPage />)} />
          <Route path="/equipment" element={<EquipmentGate><EquipmentPage /></EquipmentGate>} />
          <Route path="/equipment/new" element={<EquipmentGate><EquipmentStartPage /></EquipmentGate>} />
          <Route path="/equipment/orders/:id" element={<EquipmentGate><EquipmentOrderPage /></EquipmentGate>} />
          <Route path="/verify-form" element={<VerifyFormPage />} />
          {FEATURES.salesModule && (
            <>
              <Route path="/manage/products" element={gate(PERM.products, <ProductsPage />)} />
              <Route path="/manage/products/new" element={gate(PERM.products, <ProductEditPage />)} />
              <Route path="/manage/products/:id" element={gate(PERM.products, <ProductEditPage />)} />
              <Route path="/manage/sales-campaigns" element={gate(PERM.salesCampaigns, <SalesCampaignsPage />)} />
              <Route path="/manage/sales-campaigns/new" element={gate(PERM.salesCampaigns, <SalesCampaignEditPage />)} />
              <Route path="/manage/sales-campaigns/:id" element={gate(PERM.salesCampaigns, <SalesCampaignEditPage />)} />
              <Route path="/manage/recurring-invoices" element={gate(PERM.recurring, <RecurringInvoicesPage />)} />
              <Route path="/manage/recurring-invoices/new" element={gate(PERM.recurring, <RecurringInvoiceEditPage />)} />
              <Route path="/manage/recurring-invoices/:id" element={gate(PERM.recurring, <RecurringInvoiceEditPage />)} />
              <Route path="/reports/residuals" element={gate(PERM.residuals, <ResidualsReportPage />)} />
            </>
          )}
          {FEATURES.manageModule && (
            <>
              <Route path="/manage/tax-rates" element={gate(PERM.manageModule, <TaxRatesPage />)} />
              <Route path="/manage/audit-log" element={gate(PERM.manageModule, <AuditLogPage />)} />
            </>
          )}
          <Route path="/me" element={<MyAccountPage />} />
          {FEATURES.applicationCampaigns && (
            <>
              <Route path="/manage/application-campaigns" element={gate(PERM.appCampaigns, <CampaignsPage />)} />
              <Route path="/manage/application-campaigns/new" element={gate(PERM.appCampaigns, <CampaignEditPage />)} />
              <Route path="/manage/application-campaigns/:id" element={gate(PERM.appCampaigns, <CampaignEditPage />)} />
            </>
          )}
          <Route path="/applications" element={<ApplicationsPage />} />
          <Route path="/applications/:id" element={<ApplicationDetailPage />} />
          <Route path="/applications/v2" element={<ApplicationsV2Page />} />
          <Route path="/applications/v2/bank-check-audit" element={<BankCheckAuditPage />} />
          <Route path="/applications/v2/:id" element={<ApplicationV2WizardPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
